import { Flex } from '@mantine/core';
import { Fragment, useCallback, useMemo } from 'react';
import { BORDER_COLOR, LEFT_SECTION_WIDTH } from '../tableConstants';
import { getColumnEntries, parseQuestionDisplayName } from '../tableUtils';
import { useCellNavigation } from '../useCellNavigation';
import { SingleFundHeaderRow } from '../components/SingleFundHeaderRow';
import { MultiFundRow } from '../components/MultiFundRow';
import { SingleFundRow } from '../components/SingleFundRow';
import { QuestionHeader } from '../components/QuestionHeader';
import { idCreator } from '../../../../../Project/utils/idCreator';

const MultiFundQuestion = ({
  question,
  schema,
  funds,
  fundKeys,
  tableName,
  levelIndex,
  handleCellChange,
  cellRefs,
  onCellKeyDown,
  dataIndex
}) => {
  const { columns, children } = question;
  const columnEntries = getColumnEntries(columns);
  const keyPrefix = question.level ?? question.name;

  return (
    <>
      {!children &&
        columnEntries.map(([columnKey, columnSchema], index) => (
          <MultiFundRow
            key={`${tableName}-${index}-${columnKey}`}
            columnSchema={columnSchema}
            cellSchema={schema.properties[columnKey]}
            levelIndex={levelIndex}
            funds={funds}
            fundKeys={fundKeys}
            getValue={fundKey => funds?.[fundKey]?.fund_data?.[tableName]?.[dataIndex] || {}}
            onFundChange={(fundKey, value) =>
              handleCellChange(columnKey, value, fundKey, dataIndex)
            }
            readOnly={Boolean(columnSchema?.disabled)}
            cellRefs={cellRefs}
            getCellKey={fundKey => `${keyPrefix}-${columnKey}-${fundKey}`}
            onCellKeyDown={onCellKeyDown}
            tableName={tableName}
            fieldName={columnKey}
            hasDrillDownFeature={schema.properties[columnKey]?.has_drill_down_feature}
            symbol={schema.properties[columnKey]?.symbol}
          />
        ))}

      {children &&
        children.map(childQuestion => (
          <Fragment key={`${keyPrefix}-${childQuestion.level ?? childQuestion.name}`}>
            <MultiFundQuestion
              question={childQuestion}
              schema={schema}
              funds={funds}
              fundKeys={fundKeys}
              tableName={tableName}
              levelIndex={levelIndex + 1}
              handleCellChange={handleCellChange}
              cellRefs={cellRefs}
              onCellKeyDown={onCellKeyDown}
            />
          </Fragment>
        ))}
    </>
  );
};

const SingleFundQuestion = ({
  question,
  schema,
  data,
  levelIndex,
  handleCellChange,
  cellRefs,
  onCellKeyDown,
  fund,
  tableName,
  dataIndex
}) => {
  const { columns, children, level, name } = question;
  const columnEntries = getColumnEntries(columns);
  const keyPrefix = question.level || question.name;
  const fundId = data?.fund_id || fund?.fund_id;
  const fundName = data?.fund_name || fund?.fund_name;

  return (
    <>
      <SingleFundHeaderRow levelIndex={levelIndex} level={level} name={name} />

      {!children &&
        columnEntries.map(([columnKey, columnSchema]) => (
          <SingleFundRow
            key={`${keyPrefix}-${columnKey}`}
            columnSchema={columnSchema}
            cellSchema={schema.properties[columnKey]}
            levelIndex={levelIndex}
            value={data?.[columnKey]}
            error={data?.errors?.[columnKey]}
            onChange={value => handleCellChange(columnKey, value, null, dataIndex)}
            readOnly={Boolean(columnSchema?.disabled)}
            cellRefs={cellRefs}
            cellKey={`${keyPrefix}-${columnKey}`}
            onCellKeyDown={onCellKeyDown}
            fundId={fundId}
            fundName={fundName}
            tableName={tableName}
            fieldName={columnKey}
            tableId={data?.['id']}
            reportId={data?.['report_id']}
            hasDrillDownFeature={schema.properties[columnKey]?.has_drill_down_feature}
            isFromStaticTemplate={data?.static_template_fields?.includes(columnKey)}
            symbol={schema.properties[columnKey]?.symbol}
          />
        ))}

      {children &&
        children.map(childQuestion => (
          <Fragment key={`${keyPrefix}-${childQuestion.level ?? childQuestion.name}`}>
            <SingleFundQuestion
              question={childQuestion}
              schema={schema}
              data={data}
              levelIndex={levelIndex + 1}
              handleCellChange={handleCellChange}
              cellRefs={cellRefs}
              onCellKeyDown={onCellKeyDown}
              fund={fund}
              tableName={tableName}
            />
          </Fragment>
        ))}
    </>
  );
};

export const SecuredBorrowingTable = ({
  schema,
  fundId,
  isMultiFunds,
  funds,
  tableName,
  onChangeFund,
  name,
  questionLevels = [],
  isQuestionOpen = true,
  onToggleQuestion
}) => {
  const fundKeys = useMemo(() => Object.keys(funds || {}), [funds]);

  const { cellRefs, registerCell, onCellKeyDown } = useCellNavigation({ isMultiFunds, fundKeys });

  const handleCellChange = useCallback(
    (columnKey, value, targetFundId, index) => {
      const resolvedFundId = targetFundId || fundId;

      const currentTable = funds?.[resolvedFundId]?.fund_data?.[tableName] || [];
      const nextTable = currentTable.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [columnKey]: value } : row
      );
      onChangeFund(tableName, nextTable, resolvedFundId);
    },
    [fundId, funds, isMultiFunds, onChangeFund, tableName]
  );

  const { questionNumber, questionName } = parseQuestionDisplayName(name);

  if (isMultiFunds) {
    const isWidthFit =
      fundKeys.length > 3 && window.innerWidth < fundKeys.length * 260 + LEFT_SECTION_WIDTH;

    return (
      <Flex
        id={`formpf-secured-borrowing-table-${idCreator(tableName)}-multi`}
        direction="column"
        w={isWidthFit ? 'fit-content' : 'auto'}
        bd={`1px solid ${BORDER_COLOR}`}
      >
        <QuestionHeader
          id={`formpf-secured-borrowing-table-header-${idCreator(tableName)}-multi`}
          name={questionName}
          number={questionNumber}
          fundKeys={fundKeys}
          funds={funds}
          isMultiFunds={isMultiFunds}
          isOpen={isQuestionOpen}
          onToggle={onToggleQuestion}
        />
        {isQuestionOpen &&
          funds?.[fundId]?.fund_data?.[tableName]?.map((item, index) => (
            <Fragment key={`${tableName}-${index}-multi-view`}>
              {questionLevels.map((question, questionIndex) => (
                <Fragment key={`${tableName}-${index}-${questionIndex}-question-multi-view`}>
                  <MultiFundQuestion
                    question={question}
                    schema={schema}
                    funds={funds}
                    fundKeys={fundKeys}
                    tableName={tableName}
                    levelIndex={0}
                    handleCellChange={handleCellChange}
                    cellRefs={{ current: cellRefs, register: registerCell }}
                    onCellKeyDown={onCellKeyDown}
                    dataIndex={index}
                  />
                </Fragment>
              ))}
            </Fragment>
          ))}
      </Flex>
    );
  }

  return (
    <Flex
      id={`formpf-secured-borrowing-table-${idCreator(tableName)}-single`}
      direction="column"
      bd={`1px solid ${BORDER_COLOR}`}
    >
      <QuestionHeader
        id={`formpf-secured-borrowing-table-header-${idCreator(tableName)}-single`}
        number={questionNumber}
        name={questionName}
        isMultiFunds={isMultiFunds}
        isOpen={isQuestionOpen}
        onToggle={onToggleQuestion}
      />
      {isQuestionOpen &&
        funds?.[fundId]?.fund_data?.[tableName]?.map((item, index) => (
          <Fragment key={`${tableName}-${index}`}>
            {questionLevels.map((question, questionIndex) => (
              <Fragment key={`${tableName}-${index}-${questionIndex}-question`}>
                <SingleFundQuestion
                  question={question}
                  schema={schema}
                  data={item}
                  levelIndex={!question.level && !question.name ? -1 : 0}
                  handleCellChange={handleCellChange}
                  cellRefs={{ current: cellRefs, register: registerCell }}
                  onCellKeyDown={onCellKeyDown}
                  fund={funds?.[fundId]}
                  tableName={tableName}
                  dataIndex={index}
                />
              </Fragment>
            ))}
          </Fragment>
        ))}
    </Flex>
  );
};
